//@flow
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {
  FormControlLabel,
  makeStyles,
  Typography,
  Hidden,
  Link as MUILink,
} from '@material-ui/core';
import {Delete, Edit} from '@material-ui/icons';

import {colors} from '../../../../../../assets/styles/colors';

import type {StoreLocation} from '../../../../../../entities/store-location';
import ConfirmModal from '../../../../../../elements/Modals/ConfirmModal';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';

type Props = {
  location: StoreLocation,
  onEdit?: () => void,
  onDelete: () => void,
  onToggleEnabled: () => void,
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: '.5rem',
    position: 'relative',
    cursor: 'pointer',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: colors.white,
    transition: 'opacity .2s ease-in-out',
    flexDirection: 'column',
    justifyContent: 'space-between',
    border: `1px solid ${colors.grey200}`,

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '.75rem',
    overflow: 'hidden',

    '& img': {
      height: '200%',
      width: '100%',
      clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
      objectFit: 'cover',
      borderRadius: 'inherit',
    },
  },
  data: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexGrow: 1,
  },
  title: {
    fontSize: '14px',
    fontWeight: '500',
  },
  subtitle: {
    fontSize: '14px',
  },
  subtitleDisabled: {
    color: colors.warning,
    fontWeight: 600,
    fontSize: '14px',
  },

  actions: {
    display: 'flex',
    padding: '.25rem',
    height: '120px',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    cursor: 'auto',
  },
  enableSwitch: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    margin: 0,

    '& .label': {
      fontSize: '14px',
    },

    '& span': {
      margin: 0,
    },
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '.25rem',
    borderRadius: '99px',
    textDecoration: 'none',

    '&:hover': {
      '&[data-action="edit"]': {
        background: `${colors.primary}1c`,
      },

      '&[data-action="delete"]': {
        background: `${colors.error}1c`,
      },
    },
  },
  deleteIconButton: {
    color: colors.error,
    background: `${colors.error}26`,
    borderRadius: '50%',
    padding: '.25rem',
  },
  editLabelButton: {
    fontSize: '14px',
    textDecoration: 'none',
    color: colors.primary,
  },
  deleteLabelButton: {
    fontSize: '14px',
    fontWeight: '500',
  },

  editIconButton: {
    color: colors.primary,
    background: `${colors.primary}26`,
    borderRadius: '50%',
    padding: '.25rem',
  },

  switchLabel: {
    fontSize: '14px',
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '.5rem',
    width: '100%',
    flexDirection: 'column',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  itemData: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
}));

export default function StoreLocationItem(props: Props) {
  const {location, onDelete, onToggleEnabled} = props;

  const classes = useStyles();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const {address, name, schedule, mapUrl, enabled, phone} = location;

  return (
    <>
      <div className={classes.container}>
        <div className={classes.itemData}>
          <div className={classes.data}>
            <Typography className={classes.title}>{name}</Typography>
            <Typography className={classes.subtitle}>{address}</Typography>
            {schedule && (
              <Typography className={classes.subtitle}>
                Horas: {schedule}
              </Typography>
            )}
            {mapUrl && (
              <Typography className={classes.subtitle}>
                Url:{' '}
                <MUILink target="_blank" href={mapUrl}>
                  {mapUrl}
                </MUILink>
              </Typography>
            )}
            {phone && (
              <Typography className={classes.subtitle}>
                Teléfono: {phone}
              </Typography>
            )}
          </div>
        </div>

        <Hidden smDown>
          <footer
            onClick={e => {
              e.stopPropagation();
            }}
            className={classes.actions}
          >
            <FormControlLabel
              labelPlacement="start"
              control={
                <div className={classes.switch}>
                  <IosSwitch checked={enabled} onChange={onToggleEnabled} />
                </div>
              }
              classes={{
                label: classes.switchLabel,
              }}
              label={enabled ? 'Habilitado' : 'Deshabilitado'}
            />
            <Link
              className={classes.actionButton}
              to={`/my-store/pages/home-page/location/${location.id}`}
            >
              <span className={classes.editLabelButton}>Editar</span>
              <Edit className={classes.editIconButton} />
            </Link>
            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={e => {
                e.stopPropagation();
                openConfirmModal();
              }}
            >
              <span className={classes.deleteLabelButton}>Eliminar</span>
              <Delete className={classes.deleteIconButton} />
            </button>
          </footer>
        </Hidden>
        <Hidden mdUp>
          <footer
            onClick={e => {
              e.stopPropagation();
            }}
            className={classes.mobileActions}
          >
            <FormControlLabel
              labelPlacement="end"
              control={
                <div className={classes.switch}>
                  <IosSwitch checked={enabled} onChange={onToggleEnabled} />
                </div>
              }
              className={classes.enableSwitch}
              classes={{
                label: classes.switchLabel,
              }}
              label={enabled ? 'Habilitado' : 'Deshabilitado'}
            />
            <Link
              className={classes.actionButton}
              to={`/my-store/pages/home-page/location/${location.id}`}
            >
              <Edit className={classes.editIconButton} />
              <span className={classes.editLabelButton}>Editar</span>
            </Link>
            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={e => {
                e.stopPropagation();
                openConfirmModal();
              }}
            >
              <Delete className={classes.deleteIconButton} />
              <span className={classes.deleteLabelButton}>Eliminar</span>
            </button>
          </footer>
        </Hidden>
      </div>

      <ConfirmModal
        open={confirmModalOpen}
        title="¿Estás seguro de eliminar este banner?"
        message="Esta acción no se puede deshacer"
        onConfirm={onDelete}
        onClose={() => closeConfirmModal()}
        onCancel={() => closeConfirmModal()}
      />
    </>
  );
}
